const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DEFAULT_WARNING_THRESHOLD_DAYS = 3;
const EXPIRY_FIELD_NAMES = [
  "NgaySD",
  "NgayHetHan",
  "HanSD",
  "HSD",
  "expiryDate",
  "expirationDate",
];

const normalizeLotId = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";

  const numericValue = Number(text);
  return Number.isFinite(numericValue) ? String(numericValue) : text;
};

const getStockKey = (storeId, productId, loSX) =>
  `${String(storeId ?? "").trim()}\u001f${String(productId ?? "")
    .trim()
    .toUpperCase()}\u001f${normalizeLotId(loSX)}`;

const getExpiryValue = (entity) => {
  if (!entity) return "";

  for (const fieldName of EXPIRY_FIELD_NAMES) {
    const value = entity[fieldName];
    if (String(value ?? "").trim()) return value;
  }

  return "";
};

const parseExpiryDateParts = (value) => {
  const text = String(value ?? "").trim();
  const isoMatch = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  const localMatch = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);

  if (isoMatch) {
    return {
      year: Number(isoMatch[1]),
      month: Number(isoMatch[2]),
      day: Number(isoMatch[3]),
    };
  }

  if (localMatch) {
    return {
      year: Number(localMatch[3]),
      month: Number(localMatch[2]),
      day: Number(localMatch[1]),
    };
  }

  return null;
};

export const getExpiryDetails = (value, referenceDate = new Date()) => {
  const dateParts = parseExpiryDateParts(value);

  if (!dateParts) {
    return {
      daysRemaining: null,
      dateLabel: "Chưa xác định",
      remainingLabel: "Chưa xác định",
      statusClassName: "is-unknown",
    };
  }

  const { year, month, day } = dateParts;
  const expiryTime = Date.UTC(year, month - 1, day);
  const expiryDate = new Date(expiryTime);

  if (
    expiryDate.getUTCFullYear() !== year ||
    expiryDate.getUTCMonth() !== month - 1 ||
    expiryDate.getUTCDate() !== day
  ) {
    return {
      daysRemaining: null,
      dateLabel: "Chưa xác định",
      remainingLabel: "Chưa xác định",
      statusClassName: "is-unknown",
    };
  }

  const todayTime = Date.UTC(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  );
  const daysRemaining = Math.round((expiryTime - todayTime) / DAY_IN_MS);
  const remainingLabel =
    daysRemaining < 0
      ? `Đã hết hạn ${Math.abs(daysRemaining)} ngày`
      : daysRemaining === 0
      ? "Hết hạn hôm nay"
      : `Còn ${daysRemaining} ngày`;
  const statusClassName =
    daysRemaining <= 3
      ? "is-critical"
      : daysRemaining <= 7
      ? "is-urgent"
      : daysRemaining <= 14
      ? "is-warning"
      : "is-cool";

  return {
    daysRemaining,
    dateLabel: `${String(day).padStart(2, "0")}/${String(month).padStart(
      2,
      "0"
    )}/${year}`,
    remainingLabel,
    statusClassName,
  };
};

export const getSaleExpiryIssues = ({
  items = [],
  stockRows = [],
  storeId = "",
  referenceDate = new Date(),
  warningThresholdDays = DEFAULT_WARNING_THRESHOLD_DAYS,
}) => {
  const stockRowsByItemKey = new Map(
    stockRows.map((row) => [
      getStockKey(row.MaCH ?? storeId, row.MaSP, row.LoSX),
      row,
    ])
  );

  const issues = items.flatMap((item) => {
    const stockRow = stockRowsByItemKey.get(
      getStockKey(storeId, item.id, item.loSX)
    );
    const expiryValue =
      getExpiryValue(stockRow) ||
      getExpiryValue(item) ||
      getExpiryValue(item.raw);
    const expiryDetails = getExpiryDetails(expiryValue, referenceDate);

    if (expiryDetails.daysRemaining === null) return [];

    return [
      {
        ...item,
        expiryValue,
        ...expiryDetails,
      },
    ];
  });

  return {
    expiredItems: issues.filter((item) => item.daysRemaining < 0),
    expiringSoonItems: issues.filter(
      (item) =>
        item.daysRemaining >= 0 && item.daysRemaining < warningThresholdDays
    ),
  };
};
